import { dataViewState } from "../dataState";
import { supabase } from "../lib/supabase";
import { fetchAllRows } from "../lib/fetchAllRows";
import { isMissingTable } from "../lib/dbErrors";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toWholeCents } from "../money";
import { accountForTransaction, buildLaborLookup, classifyCost } from "../laborRules";
import { KINDS, countedLaborRows, emptyEntries, foldEntriesIntoJobs, rowToEntry } from "../savedEntries";

// ─── SUPABASE DATA HOOK ───────────────────────────────────────────────────────

function useContractorData(userId) {
  const [liveJobSummaries, setLiveJobSummaries] = useState(null);
  const [liveAutoMatched, setLiveAutoMatched]   = useState([]);
  const [liveSuggested, setLiveSuggested]       = useState([]);
  const [liveUntagged, setLiveUntagged]         = useState(null);
  const [liveTagged, setLiveTagged]             = useState([]);
  const [liveOverhead, setLiveOverhead]         = useState([]);
  const [liveDismissed, setLiveDismissed]       = useState([]);
  const [liveVendorRules, setLiveVendorRules]   = useState([]);
  const [savedEntries, setSavedEntries]         = useState(emptyEntries); // labor, cash costs and added revenue the owner entered in Canopy
  const [liveLaborRules, setLiveLaborRules]   = useState([]); // the owner's labor choices; they also decide how Canopy-entered cash costs split
  const [loading, setLoading]                   = useState(false);
  const [dataSource, setDataSource]             = useState('none'); // 'live' only when the account's own jobs are on screen
  const [loaded, setLoaded]                     = useState(false);  // the first load for this account has finished
  const [loadError, setLoadError]               = useState(false);  // the last load failed

  const currentUserRef = useRef(userId);
  currentUserRef.current = userId;

  // Returns true when the account's own jobs are now on screen. Results for a different account are dropped.
  async function loadLiveData() {
    if (!userId) return false;
    const forUser = userId;
    const stale = () => currentUserRef.current !== forUser;
    setLoading(true);
    setLoadError(false);
    try {
      const { data: jobs, error: jobsError } = await fetchAllRows(() => supabase
        .from('jobs').select('*').eq('contractor_id', userId));

      if (stale()) return false;
      if (jobsError) throw jobsError;
      if (!jobs || jobs.length === 0) {
        // Loaded fine; this account has no jobs yet. Show the empty state, never sample jobs.
        setLiveJobSummaries([]); setLiveAutoMatched([]); setLiveSuggested([]); setLiveUntagged([]);
        setLiveTagged([]); setLiveOverhead([]); setLiveDismissed([]); setLiveVendorRules([]); setSavedEntries(emptyEntries()); setLiveLaborRules([]);
        setDataSource('none');
        setLoaded(true);
        setLoading(false);
        return false;
      }

      const { data: transactions, error: txnError } = await fetchAllRows(() => supabase
        .from('transactions').select('*').eq('contractor_id', userId));
      if (stale()) return false;
      if (txnError) throw txnError;

      // Fetch all inbox items (every page, not just the first 1,000), split by status client-side
      const { data: allInboxItems, error: inboxError } = await fetchAllRows(() => supabase
        .from('inbox_tags')
        .select('*')
        .eq('contractor_id', userId));
      if (stale()) return false;
      if (inboxError) throw inboxError;

      const autoMatchedItems = [];
      const suggestedItems   = [];
      const pendingItems     = [];
      const taggedItems      = [];
      const overheadItems    = [];
      const dismissedItems   = [];

      (allInboxItems || []).forEach(item => {
        switch (item.status) {
          case 'auto_matched': autoMatchedItems.push(item); break;
          case 'suggested':    suggestedItems.push(item);   break;
          case 'pending':      pendingItems.push(item);     break;
          case 'tagged':       taggedItems.push(item);      break;
          case 'overhead':     overheadItems.push(item);    break;
          case 'dismissed':    dismissedItems.push(item);   break;
          default:             pendingItems.push(item);     break;
        }
      });

      // Fetch vendor rules (graceful — table may not exist yet)
      // Only a missing table is skipped; any other error fails the load like the other reads.
      let vendorRulesData = [];
      {
        const { data: vrData, error: vrError } = await fetchAllRows(() => supabase
          .from('vendor_rules')
          .select('*')
          .eq('contractor_id', userId), { orderBy: [{ column: 'vendor_name' }] });
        if (vrError && !isMissingTable(vrError)) throw vrError;
        vendorRulesData = vrData || [];
      }

      // Labor rules (graceful: the table may not exist yet, then only the category keywords apply)
      let laborRuleRows = [];
      {
        const { data: lrData, error: lrError } = await fetchAllRows(() => supabase
          .from('labor_rules').select('*').eq('contractor_id', userId));
        if (lrError && !isMissingTable(lrError)) throw lrError;
        laborRuleRows = lrData || [];
      }
      if (stale()) return false;
      const laborLookup = buildLaborLookup(laborRuleRows);

      // Entries made in Canopy. Graceful: if a table does not exist yet (the update has not been applied) it counts
      // as no saved entries. Any other error fails the load, like the other reads, so numbers are never cut short.
      const loadedEntries = emptyEntries();
      for (const kind of Object.keys(KINDS)) {
        const { data: rows, error: entryError } = await fetchAllRows(() => supabase
          .from(KINDS[kind].table).select('*').eq('contractor_id', userId));
        if (entryError && !isMissingTable(entryError)) throw entryError;
        const usable = kind === 'labor' ? countedLaborRows(rows) : (rows || []);
        loadedEntries[kind] = usable.map(r => rowToEntry(kind, r));
      }
      if (stale()) return false;
      // Canopy-made cost rows take their QuickBooks category from the review item they came from
      const reviewAccounts = {};
      (allInboxItems || []).forEach(it => { if (it.account_name) reviewAccounts[it.id] = it.account_name; });

      const liveSummaries = jobs.map(job => {
        const jobTxns   = (transactions || []).filter(t => t.job_id === job.id);
        const invoices  = jobTxns.filter(t => t.type === 'revenue');
        const expenses  = jobTxns.filter(t => t.type === 'expense');
        // Whole cents per row, added as integers, divided once: 0.1 + 0.2 is exactly 0.3
        const revenueCents = invoices.reduce((s, t) => s + toWholeCents(t.amount || 0), 0);
        const revenue   = revenueCents / 100;
        // Every cost line is labor or materials, never both. Whole cents, so the two always add up to costs.
        const lineIsLabor = new Map();
        let laborCents = 0, materialCents = 0;
        expenses.forEach(t => {
          const account = accountForTransaction(t, userId, reviewAccounts);
          const { isLabor } = classifyCost({ vendor: t.vendor, accountName: account }, laborLookup);
          lineIsLabor.set(t.id, isLabor);
          const c = toWholeCents(t.amount || 0);
          if (isLabor) laborCents += c; else materialCents += c;
        });
        const costs     = (laborCents + materialCents) / 100;
        const profit    = (revenueCents - laborCents - materialCents) / 100;
        const marginPct = revenue > 0 ? ((profit / revenue) * 100).toFixed(1) : '0.0';
        const vendorCents = {};
        expenses.forEach(t => {
          if (t.vendor) vendorCents[t.vendor] = (vendorCents[t.vendor] || 0) + toWholeCents(t.amount || 0);
        });
        const costByVendor = {};
        Object.keys(vendorCents).forEach(v => { costByVendor[v] = vendorCents[v] / 100; });
        const invoiceObjs = invoices.map(t => ({
          Id: t.id, DocNumber: t.doc_number, TxnDate: t.txn_date,
          TotalAmt: t.amount, Balance: 0,
          Line: [{ Description: t.description, Amount: t.amount }],
          CustomerRef: { value: job.qb_job_id || job.id }
        }));
        const purchaseObjs = expenses.map(t => ({
          Id: t.id, DocNumber: t.doc_number, TxnDate: t.txn_date,
          TotalAmt: t.amount, EntityRef: { name: t.vendor || 'Unknown' },
          Line: [{ Amount: t.amount, Description: t.description, isLabor: lineIsLabor.get(t.id) === true,
            AccountBasedExpenseLineDetail: { CustomerRef: { value: job.id } } }]
        }));
        return {
          id: job.id, name: job.name, clientName: job.client_name || '',
          type: job.job_type || 'General', status: job.status || 'Complete',
          revenue, costs, materialCost: materialCents / 100, laborCost: laborCents / 100, profit, marginPct, outstanding: 0,
          invoices: invoiceObjs, purchases: purchaseObjs, laborEntries: [], manualExpenses: [], manualRevenue: [], costByVendor,
          firstDate: invoices[0]?.txn_date || '', lastDate: invoices[invoices.length-1]?.txn_date || '',
        };
      });

      // Parse inbox items into frontend shape
      function parseInboxItem(item) {
        return {
          id: item.id, docNumber: item.doc_number, vendor: item.vendor,
          date: item.txn_date, amount: item.amount, description: item.description,
          paymentType: item.payment_type || 'Check',
          suggestedJob: item.suggested_job_id, suggestionReason: item.suggestion_reason,
          taggedJobId: item.tagged_job_id || null,
          confidence: item.confidence || null, matchTier: item.match_tier || null,
          matchReason: item.match_reason || null, matchedBy: item.matched_by || null,
        };
      }

      if (stale()) return false; // a late load for a previous account must not put its jobs on screen
      const shown = foldEntriesIntoJobs(liveSummaries, loadedEntries, laborLookup);
      setSavedEntries(loadedEntries);
      setLiveLaborRules(laborRuleRows);
      setLiveJobSummaries(liveSummaries);
      setLiveAutoMatched(autoMatchedItems.map(parseInboxItem));
      setLiveSuggested(suggestedItems.map(parseInboxItem));
      setLiveUntagged(pendingItems.map(parseInboxItem));
      setLiveTagged(taggedItems.map(parseInboxItem));
      setLiveOverhead(overheadItems.map(parseInboxItem));
      setLiveDismissed(dismissedItems.map(parseInboxItem));
      setLiveVendorRules(vendorRulesData);
      setDataSource(shown.length > 0 ? 'live' : 'none');
      setLoaded(true);
      setLoading(false);
      return shown.length > 0;
    } catch (err) {
      console.error('Error loading live data:', err);
      if (stale()) return false;
      setLoadError(true);
    }
    setLoading(false);
    return false;
  }

  useEffect(() => {
    // A different account (or signing out) starts from a clean slate: never carry one account's numbers over.
    setLiveJobSummaries(null); setLiveAutoMatched([]); setLiveSuggested([]); setLiveUntagged(null);
    setLiveTagged([]); setLiveOverhead([]); setLiveDismissed([]); setLiveVendorRules([]); setSavedEntries(emptyEntries()); setLiveLaborRules([]);
    setDataSource('none'); setLoaded(false); setLoadError(false);
    loadLiveData();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // A saved entry is put on screen (or taken off) only after the database confirmed it, so the screen never shows
  // an entry that was not saved. The totals use the same sums as the load, so a reload gives the same numbers.
  const putSavedEntry = useCallback((kind, entry) => {
    setSavedEntries(prev => ({ ...prev, [kind]: [...prev[kind].filter(e => e.id !== entry.id), entry] }));
  }, []);
  const dropSavedEntry = useCallback((kind, id) => {
    setSavedEntries(prev => ({ ...prev, [kind]: prev[kind].filter(e => e.id !== id) }));
  }, []);

  const summaries = useMemo(
    () => (liveJobSummaries ? foldEntriesIntoJobs(liveJobSummaries, savedEntries, buildLaborLookup(liveLaborRules)) : []),
    [liveJobSummaries, savedEntries, liveLaborRules]
  );
  return {
    jobSummaries: summaries,
    viewState: dataViewState({ loaded, loadError, jobCount: summaries.length }),
    autoMatched:  liveAutoMatched,
    suggested:    liveSuggested,
    untagged:     liveUntagged     || [],
    tagged:       liveTagged,
    overhead:     liveOverhead,
    dismissed:    liveDismissed,
    vendorRules:  liveVendorRules,
    loading,
    dataSource,
    putSavedEntry,
    dropSavedEntry,
    refresh: loadLiveData,
  };
}

export { useContractorData };
